
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Language, Content } from '@/types';
import { Globe, Menu, X, Sun, Moon, GraduationCap, Building2, ChevronRight, Zap, Terminal, Map, Compass, Sprout, FileText, Database, Search, BookOpen, ShieldCheck, Droplets, BarChart3, TrendingUp, Shield, Umbrella, ArrowUpRight, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MegaMenu } from './MegaMenu';
import { MobileMenu } from './MobileMenu';

/* ═══════════════════════════════════════════════════════════════════════════
   VEXL 2026 — NAVBAR COMPONENT
   "Frosted Obsidian" Glassmorphism Navigation
   ═══════════════════════════════════════════════════════════════════════════ */

export interface MegaMenuItem {
  id?: string;
  title: string;
  path: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  desc?: string;
  isExternal?: boolean;
}

export interface MegaMenuSection {
  title: string;
  items: MegaMenuItem[];
}

export interface MegaMenuConfig {
  sections: MegaMenuSection[];
}

export interface NavItem {
  path: string;
  label: string;
  megaMenu?: MegaMenuConfig;
}

interface NavbarProps {
  lang: Language;
  setLang: (lang: Language) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  content: Content['nav'];
  isLoading: boolean;
  isAcademy?: boolean;
  isAdmin?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Magnetic Link Component — Subtle mouse-follow effect
// ─────────────────────────────────────────────────────────────────────────────
interface MagneticLinkProps {
  children: React.ReactNode;
  to: string;
  isActive: boolean;
  isHovered: boolean;
  isAnyHovered: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onClick?: () => void;
  isAcademy?: boolean;
  hasMegaMenu?: boolean;
}

const MagneticLink: React.FC<MagneticLinkProps> = ({
  children, to, isActive, isHovered, isAnyHovered, onClick, hasMegaMenu
}) => {
  const sharedClassName = `
    relative py-2 px-3 text-[11px] font-bold tracking-[0.15em] uppercase
    transition-all duration-200 block w-auto text-center origin-center cursor-pointer
    ${isAnyHovered
      ? isHovered
        ? 'text-slate-950 dark:text-white opacity-100'
        : 'text-slate-900 dark:text-white opacity-40'
      : isActive
        ? 'text-slate-950 dark:text-white opacity-100'
        : 'text-slate-900 dark:text-white opacity-90'
    }
  `;

  // Items with a mega menu must NOT navigate — they only trigger hover dropdowns
  if (hasMegaMenu) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={sharedClassName}
      >
        <span className="relative z-10 flex items-center justify-center gap-2 w-full">{children}</span>
      </button>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={sharedClassName}
    >
      <span className="relative z-10 flex items-center justify-center gap-2 w-full">{children}</span>
    </Link>
  );
};




// ─────────────────────────────────────────────────────────────────────────────
// Theme Segmented Control — Light vs Dark
// ─────────────────────────────────────────────────────────────────────────────
interface ThemeSegmentedToggleProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isAcademy: boolean;
}

const ThemeSegmentedToggle: React.FC<ThemeSegmentedToggleProps> = ({ theme, toggleTheme, isAcademy }) => {
  const isDark = theme === 'dark';
  const activeThumbColor = isAcademy ? 'glass-segmented-thumb-purple' : 'glass-segmented-thumb-cyan';

  return (
    <div className="glass-segmented-control select-none relative">
      {/* Light Theme Button (Sun) */}
      <button
        onClick={() => { if (isDark) toggleTheme(); }}
        className={`glass-segmented-btn relative px-3.5 ${!isDark ? 'active' : ''}`}
        aria-label="Light theme"
      >
        {!isDark && (
          <motion.div
            layoutId="themeToggleActiveThumb"
            className={`absolute inset-0 glass-segmented-thumb ${activeThumbColor}`}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.45 }}
          />
        )}
        <span className="relative z-10 flex items-center justify-center">
          <Sun size={13} className="transition-transform duration-300 group-hover:rotate-12" />
        </span>
      </button>

      {/* Dark Theme Button (Moon) */}
      <button
        onClick={() => { if (!isDark) toggleTheme(); }}
        className={`glass-segmented-btn relative px-3.5 ${isDark ? 'active' : ''}`}
        aria-label="Dark theme"
      >
        {isDark && (
          <motion.div
            layoutId="themeToggleActiveThumb"
            className={`absolute inset-0 glass-segmented-thumb ${activeThumbColor}`}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.45 }}
          />
        )}
        <span className="relative z-10 flex items-center justify-center">
          <Moon size={13} className="transition-transform duration-300 group-hover:-rotate-12" />
        </span>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Navbar Component
// ─────────────────────────────────────────────────────────────────────────────
const Navbar: React.FC<NavbarProps> = ({
  lang, setLang, theme, toggleTheme, content, isLoading, isAcademy = false, isAdmin = false
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const enterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ─── Hover Intent Timing (ms) ───
  // ENTER_DWELL_MS: Intentional dwell time before triggering mega menu.
  // Brushing past to address bar takes ~30-60ms (safely ignored).
  // 140ms ensures instantaneous feel on deliberate hover.
  const ENTER_DWELL_MS = 140;
  const SWITCH_TAB_MS = 30;
  const LEAVE_GRACE_MS = 280;

  const clearAllHoverTimeouts = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  }, []);

  const handleNavEnter = useCallback((item: NavItem) => {
    // 1. Cancel any pending leave/close timer immediately
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }

    // 2. If item has no mega menu (e.g. Admin or simple link)
    if (!item.megaMenu) {
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = null;
      }
      if (hoveredPath) {
        leaveTimeoutRef.current = setTimeout(() => {
          setHoveredPath(null);
        }, 120);
      }
      return;
    }

    // 3. Already on this item
    if (hoveredPath === item.path) {
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = null;
      }
      return;
    }

    // 4. If a mega menu is ALREADY open, switch fast (60ms)
    if (hoveredPath !== null) {
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = setTimeout(() => {
        setHoveredPath(item.path);
      }, SWITCH_TAB_MS);
      return;
    }

    // 5. Initial open: require intentional dwell (220ms).
    // If the user's cursor is just flying past, they exit in <100ms and handleNavLeave cancels this!
    if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    enterTimeoutRef.current = setTimeout(() => {
      setHoveredPath(item.path);
    }, ENTER_DWELL_MS);
  }, [hoveredPath]);

  const handleNavLeave = useCallback(() => {
    // If the mouse left before the 220ms dwell elapsed, cancel opening immediately!
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }

    // If the mega menu was actually open, give grace period to travel into it
    if (hoveredPath) {
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = setTimeout(() => {
        setHoveredPath(null);
      }, LEAVE_GRACE_MS);
    }
  }, [hoveredPath]);

  const handleMenuEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  }, []);

  const handleMenuLeave = useCallback(() => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredPath(null);
    }, LEAVE_GRACE_MS);
  }, []);

  const handleMenuClose = useCallback(() => {
    clearAllHoverTimeouts();
    setHoveredPath(null);
  }, [clearAllHoverTimeouts]);

  // ─── Route Navigation Dismiss ───
  useEffect(() => {
    handleMenuClose();
    setIsMobileMenuOpen(false);
  }, [location.pathname, handleMenuClose]);

  // ─── Escape Key Dismiss ───
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleMenuClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMenuClose]);

  // ─── Cleanup on Unmount ───
  useEffect(() => {
    return () => {
      clearAllHoverTimeouts();
    };
  }, [clearAllHoverTimeouts]);

  // ─── Scroll Detection ───
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 50;
      setScrolled(scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Scroll Lock for Mobile Menu Only (Never for desktop mega-menu hover) ───
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileMenuOpen]);


  // 🧠 Animation Lock: Skip intro animations if session is already initialized
  const hasAnimated = typeof window !== 'undefined' && sessionStorage.getItem('appLoaded') === 'true';

  const isActive = (path: string) => location.pathname === path;

  const handleLogoClick = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/' || location.pathname === '/ar') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // ─── Navigation Items ───
  const navItems: NavItem[] = [
    {
      path: '#products',
      label: lang === 'en' ? 'Products' : 'المنتجات',
      megaMenu: {
        sections: [
          {
            title: lang === 'en' ? 'Core Platforms' : 'المنصات الأساسية',
            items: [
              { id: 'nova', title: 'VEXL Nova', path: '/products/nova', icon: Search, desc: lang === 'ar' ? 'منصة الاستدلال والذكاء المكاني الفائقة للمؤسسات' : 'Institutional Spatial Intelligence & Reasoning Workspace' },
              { id: 'croplens', title: 'VEXL CropLens', path: '/products/croplens', icon: Sprout, desc: lang === 'ar' ? 'رؤية وتحليلات زراعية استباقية عبر الأقمار الصناعية' : 'Satellite agricultural intelligence & field analytics' },
              { id: 'geo', title: 'VEXL GeoAgent', path: '/products/geo', icon: Compass, desc: lang === 'ar' ? 'محرك أتمتة سير العمل المكاني والتحليل الجغرافي' : 'Autonomous spatial workflow & reasoning engine' },
              { id: 'droughtwatch', title: 'VEXL DroughtWatch', path: '/products/droughtwatch', icon: Droplets, desc: lang === 'ar' ? 'التنبؤ بالمخاطر المناخية والأثر المائي للمحاصيل' : 'Predictive climate & drought risk forecasting' }
            ]
          },
          {
            title: lang === 'en' ? 'Specialized Engines' : 'المحركات المتخصصة',
            items: [
              { id: 'agroscore', title: 'AgroScore', path: '/products/agroscore', icon: BarChart3, desc: lang === 'ar' ? 'منظومة الذكاء الاصطناعي الزراعي والائتمان الفضائي' : 'Autonomous agricultural & credit intelligence' },
              { id: 'cartograph', title: 'Nova Cartograph', path: '/nova-cartograph', icon: Map, desc: lang === 'ar' ? 'بيئة الاستكشاف المكاني والخرائط التفاعلية' : 'Interactive spatial reasoning & cartographic canvas' },
              { id: 'benchmarks', title: lang === 'en' ? 'Model Benchmarks' : 'المقارنات المعيارية والدقة', path: '/benchmarks', icon: TrendingUp, desc: lang === 'ar' ? 'دقة النماذج والمقارنات المعيارية الكمية' : 'Quantitative spatial model evaluations & accuracy' }
            ]
          }
        ]
      }
    },
    {
      path: '#solutions',
      label: lang === 'en' ? 'Solutions' : 'الحلول والصناعات',
      megaMenu: {
        sections: [
          {
            title: lang === 'en' ? 'Industry Verticals' : 'القطاعات التخصصية',
            items: [
              { id: 'defense', title: lang === 'en' ? 'Defense & Intelligence' : 'الدفاع والاستخبارات', path: '/solutions/defense', icon: Shield, desc: lang === 'ar' ? 'وعي موقفي تكتيكي ومراقبة الحدود والمنشآت الحيوية' : 'Tactical Edge situational awareness & defense' },
              { id: 'enterprise', title: lang === 'en' ? 'Finance & Risk' : 'المالية وتقييم المخاطر', path: '/solutions/enterprise', icon: Building2, desc: lang === 'ar' ? 'ذكاء مكاني لتقييم المحافظ والمخاطر الائتمانية والأصول' : 'Spatial intelligence for portfolio & asset risk' },
              { id: 'urban', title: lang === 'en' ? 'Urban & Infrastructure' : 'المدن والبنية التحتية', path: '/solutions/urban', icon: Map, desc: lang === 'ar' ? 'نمذجة مكانية متقدمة وتخطيط المدن الذكية والمرافق' : 'Urban spatial modeling & smart city infrastructure' },
              { id: 'agri', title: lang === 'en' ? 'Agriculture & Food Security' : 'الزراعة والأمن الغذائي', path: '/solutions/agri', icon: Sprout, desc: lang === 'ar' ? 'زراعة دقيقة ورصد سلاسل الغذاء والمحاصيل عبر الأقمار' : 'Satellite precision farming & food security' },
              { id: 'logistics', title: lang === 'en' ? 'Logistics & Supply Chains' : 'الخدمات اللوجستية والإمداد', path: '/solutions/logistics', icon: Zap, desc: lang === 'ar' ? 'تحسين المسارات اللوجستية ومرونة سلاسل التوريد المعقدة' : 'Deep routing optimization & supply network resilience' }
            ]
          },
          {
            title: lang === 'en' ? 'Enterprise Ecosystem' : 'منظومة المؤسسات',
            items: [
              { id: 'overview', title: lang === 'en' ? 'Solutions Overview' : 'نظرة شاملة على الحلول', path: '/solutions', icon: Globe, desc: lang === 'en' ? 'Comprehensive institutional spatial solutions' : 'حلول مكانية متكاملة للمؤسسات والهيئات' },
              { id: 'case-studies', title: lang === 'en' ? 'Case Studies & Impact' : 'دراسات الأثر الميداني', path: '/case-studies', icon: TrendingUp, desc: lang === 'en' ? 'Verified real-world field outcomes and ROI' : 'دراسات الأثر والنتائج الميدانية الموثقة' },
              { id: 'resources', title: lang === 'en' ? 'Client Portal & Whitepapers' : 'بوابة العملاء والمستندات', path: '/portal', icon: FileText, desc: lang === 'en' ? 'Whitepapers, portal access & security briefs' : 'أوراق فنية وتوثيقات معمارية آمنة' },
              { id: 'pricing', title: lang === 'en' ? 'Plans & Pricing' : 'الخطط والأسعار', path: '/nova/pricing', icon: Zap, desc: lang === 'en' ? 'Workspace licensing & enterprise contracts' : 'تراخيص مساحات العمل وعقود المؤسسات' }
            ]
          }
        ]
      }
    },
    {
      path: '#research',
      label: lang === 'en' ? 'Research' : 'الأبحاث',
      megaMenu: {
        sections: [
          {
            title: lang === 'en' ? 'Scientific Labs' : 'المختبرات العلمية',
            items: [
              { id: 'research-overview', title: lang === 'en' ? 'Research Overview' : 'نظرة عامة على الأبحاث', path: '/research/overview', icon: Globe, desc: lang === 'en' ? 'VEXL GeoAI foundational research pillars' : 'أعمدة ومحاور أبحاث فكسل التأسيسية' },
              { id: 'research-index', title: lang === 'en' ? 'Publications Index' : 'فهرس المنشورات العلمية', path: '/research/index', icon: BookOpen, desc: lang === 'en' ? 'Peer-reviewed spatial research & preprints' : 'أوراق بحثية محكمة علمياً ومنشورات' },
              { id: 'research-residency', title: lang === 'en' ? 'Research Residency' : 'برنامج الإقامة البحثية', path: '/research/residency', icon: GraduationCap, desc: lang === 'en' ? 'Elite fellowship for PhD researchers' : 'برنامج زمالة لأبحاث الدكتوراه والمبتكرين' }
            ]
          },
          {
            title: lang === 'en' ? 'Sovereignty & Safety' : 'السيادة والأمان',
            items: [
              { id: 'safety', title: lang === 'en' ? 'Safety & Trust Center' : 'مركز الأمان والموثوقية', path: '/safety', icon: ShieldCheck, desc: lang === 'en' ? 'Our commitment to sovereign, verified GeoAI' : 'التزامنا بذكاء مكاني سيادي وموثوق ومحقق' },
              { id: 'security', title: lang === 'en' ? 'Data Sovereignty & Security' : 'أمن البيانات والسيادة الرقمية', path: '/security', icon: Shield, desc: lang === 'en' ? 'Zero-egress & air-gapped architecture' : 'بنية سيادية مغلقة بدون أي تسريب للبيانات' }
            ]
          }
        ]
      }
    },
    {
      path: '#developer',
      label: lang === 'en' ? 'Developers' : 'المطورين',
      megaMenu: {
        sections: [
          {
            title: lang === 'en' ? 'Build on VEXL' : 'البناء على فكسل',
            items: [
              { id: 'docs', title: lang === 'en' ? 'APIs & Documentation' : 'الواجهات البرمجية والتوثيق', path: '/docs', icon: FileText, desc: lang === 'en' ? 'Access GeoAI endpoints (Private Preview)' : 'الوصول لنقاط GeoAI (معاينة خاصة)' },
              { id: 'sdk', title: lang === 'en' ? 'SDKs & Client Libraries' : 'المكتبات البرمجية SDKs', path: '/sdk', icon: Terminal, desc: lang === 'en' ? 'Python, TypeScript & REST clients' : 'مكتبات عملاء Python و TypeScript' },
              { id: 'integrations', title: lang === 'en' ? 'GIS Integrations & Connectors' : 'تكاملات ومنظومات GIS', path: '/integrations', icon: Cpu, desc: lang === 'en' ? 'ArcPy, QGIS, GEE & PostGIS connectors' : 'تكاملات ArcPy و QGIS و GEE و PostGIS' },
              { id: 'examples', title: lang === 'en' ? 'Code Examples & Templates' : 'أمثلة ومشاريع توضيحية', path: 'https://github.com/vexl-gis', icon: Database, isExternal: true, desc: lang === 'en' ? 'Open-source boilerplates & integration guides' : 'قوالب جاهزة وأدلة تكامل مفتوحة المصدر' }
            ]
          },
          {
            title: lang === 'en' ? 'Community & Status' : 'المجتمع والتشغيل',
            items: [
              { title: lang === 'en' ? 'VEXL Discord' : 'مجتمع ديسكورد', path: 'https://discord.gg/vexl', icon: Zap, isExternal: true, desc: lang === 'en' ? 'Join developers & GeoAI engineers' : 'انضم لنخبة مهندسي الذكاء المكاني' },
              { title: lang === 'en' ? 'System Status' : 'حالة النظام والجهوزية', path: 'https://status.vexl-gis.tech', icon: Zap, isExternal: true, desc: lang === 'en' ? 'API & model operational uptime' : 'مراقبة تشغيل الخدمة ومستوى الجهوزية' },
              { title: lang === 'en' ? 'Release Notes' : 'سجل التحديثات والإصدارات', path: '/release-notes', icon: Zap, desc: lang === 'en' ? 'Latest features, improvements & changelog' : 'أحدث الميزات وتحديثات المنظومة' }
            ]
          }
        ]
      }
    },
    {
      path: '#company',
      label: lang === 'en' ? 'Company' : 'الشركة',
      megaMenu: {
        sections: [
          {
            title: lang === 'en' ? 'About VEXL' : 'عن فكسل',
            items: [
              { id: 'about', title: lang === 'en' ? 'About Us' : 'من نحن', path: '/about', icon: Building2, desc: lang === 'en' ? 'Our mission and sovereign GeoAI roots' : 'مهمتنا وجذورنا التقنية في الذكاء المكاني' },
              { id: 'ip', title: lang === 'en' ? 'Intellectual Property' : 'الملكية الفكرية', path: '/intellectual-property', icon: ShieldCheck, desc: lang === 'en' ? 'Protected software assets (EGIPA #5007)' : 'أصول برمجية مسجلة ومحمية (#5007)' },
              { id: 'vision', title: lang === 'en' ? 'Vision' : 'الرؤية التقنية', path: '/vision', icon: Zap, desc: lang === 'en' ? 'Designing the sovereign spatial internet' : 'تصميم بنية الإنترنت المكاني السيادي' },
              { id: 'careers', title: lang === 'en' ? 'Careers' : 'الوظائف الشاغرة', path: '/careers', icon: GraduationCap, desc: lang === 'en' ? 'Join the frontier of GeoAI engineering' : 'انضم إلى نخبة مهندسي الذكاء الجغرافي' }
            ]
          },
          {
            title: lang === 'en' ? 'Ecosystem & Brand' : 'المنظومة والهوية',
            items: [
              { title: lang === 'en' ? 'Brand Guidelines' : 'إرشادات الهوية والتصميم', path: '/brand', icon: Zap, desc: lang === 'en' ? 'Official Brand Guidelines & Design System' : 'الدليل الرسمي للهوية ونظام التصميم' },
              { title: lang === 'en' ? 'Newsroom' : 'غرفة الأخبار والبيانات', path: '/news', icon: Zap, desc: lang === 'en' ? 'Official announcements & press releases' : 'البيانات الصحفية والأخبار الرسمية' },
              { title: lang === 'en' ? 'Partners & Alliances' : 'الشركاء والتحالفات', path: '/partners', icon: Globe, desc: lang === 'en' ? 'Academic & enterprise alliances' : 'تحالفات الجامعات والمؤسسات والشركاء' },
              { title: lang === 'en' ? 'Contact Us' : 'تواصل معنا', path: '/contact', icon: Globe, desc: lang === 'en' ? 'Direct uplink to engineering & leadership' : 'قنوات التواصل المباشرة مع الفريق الهندسي' }
            ]
          }
        ]
      }
    }
  ];

  if (!isAcademy && isAdmin) {
    navItems.push({ path: '/admin', label: lang === 'en' ? 'Admin' : 'الإدارة' });
  }

  // ─── Dynamic Background Styles ───
  // When MegaMenu is open, Navbar MUST be completely solid opaque and seamlessly
  // fused with the MegaMenu into a single continuous piece (no bottom border, no bottom shadow, 100% solid opacity).
  const isMegaMenuOpen = Boolean(hoveredPath);

  const navbarBgClass = isMegaMenuOpen
    ? 'bg-white dark:bg-[#05070A] border-b border-transparent shadow-none'
    : scrolled
      ? 'bg-white/95 dark:bg-[#05070A]/95 backdrop-blur-xl border-b border-slate-200 dark:border-[#262C31]/80 shadow-[0_4px_30px_rgba(0,0,0,0.5)]'
      : 'bg-transparent border-b border-transparent';

  return (
    <>
      <motion.nav
        initial={hasAnimated ? false : { opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: isLoading ? 2.2 : 0, ease: [0.16, 1, 0.3, 1] }}
        dir="ltr"
        className={`
          fixed top-0 left-0 right-0 z-50 
          h-14 md:h-16 
          flex items-center 
          transition-colors duration-200 ease-out
          ${navbarBgClass}
        `}
      >
        <div className="w-full max-w-[1450px] mx-auto px-6 flex justify-between items-center">
          {/* ═══ Left Section: Logo + Navigation Links ═══ */}
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Logo with Minimal Sovereign Bar */}
            <Link to={lang === 'ar' ? '/ar' : '/'} onClick={handleLogoClick} className="relative z-50 group select-none">
              <div className="relative flex items-center gap-2.5" dir="ltr">
                {!isLoading && (
                  <div className="flex items-center gap-2.5 origin-left">
                    {/* Minimal Kinetic Telemetry Bar: Only moves on scroll */}
                    <motion.div
                      className="w-1.5 h-7 md:h-8 rounded-sm overflow-hidden relative"
                      animate={
                        scrolled
                          ? {
                              scaleY: [1, 1.25, 0.95, 1],
                              boxShadow: '0 0 14px rgba(14, 147, 163, 0.85)',
                            }
                          : {
                              scaleY: 1,
                              boxShadow: '0 0 0px rgba(14, 147, 163, 0)',
                            }
                      }
                      transition={{
                        duration: 0.45,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      <div className="absolute inset-0 bg-[#0E93A3]" />
                      {/* Subtle laser sweep that runs only once upon scrolling */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent"
                        animate={
                          scrolled
                            ? { y: ['-100%', '100%'], opacity: [0, 0.9, 0] }
                            : { y: '-100%', opacity: 0 }
                        }
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                      />
                    </motion.div>

                    {/* Pure Static Minimal VEXL Typography */}
                    <span className="text-2xl md:text-[1.75rem] font-black tracking-[0.2em] text-slate-900 dark:text-white m-0 leading-none block origin-center">
                      VEXL
                    </span>
                  </div>
                )}
              </div>
            </Link>

            {/* ═══ Navigation Links ═══ */}
            <motion.div
              initial={hasAnimated ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: isLoading ? 0 : 1, y: isLoading ? -10 : 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="hidden xl:flex items-center"
            >
              <div className="flex items-center gap-2 select-none relative">
                {navItems.map((item) => (
                  <div
                    key={item.path}
                    className="relative"
                    onMouseEnter={() => handleNavEnter(item)}
                    onMouseLeave={handleNavLeave}
                  >
                    <MagneticLink
                      to={item.path}
                      isActive={isActive(item.path)}
                      isHovered={hoveredPath === item.path}
                      isAnyHovered={hoveredPath !== null}
                      onHoverStart={() => { }}
                      onHoverEnd={() => { }}
                      onClick={() => {
                        if (item.megaMenu) {
                          if (hoveredPath === item.path) {
                            handleMenuClose();
                          } else {
                            clearAllHoverTimeouts();
                            setHoveredPath(item.path);
                          }
                        } else {
                          handleMenuClose();
                        }
                      }}
                      isAcademy={isAcademy}
                      hasMegaMenu={Boolean(item.megaMenu)}
                    >
                      {item.label}
                    </MagneticLink>

                    {/* ✨ Invisible Bridge to prevent mouse drop — ONLY rendered when THIS menu is active */}
                    {hoveredPath === item.path && Boolean(item.megaMenu) && (
                      <div className="absolute top-full left-0 w-full h-8 z-10 pointer-events-auto" />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ─── Global Absolute Enterprise Mega Menu ─── */}
          <MegaMenu
            hoveredPath={hoveredPath}
            setHoveredPath={setHoveredPath}
            hoverTimeout={hoverTimeout}
            onMenuEnter={handleMenuEnter}
            onMenuLeave={handleMenuLeave}
            onClose={handleMenuClose}
            navItems={navItems}
            lang={lang}
          />

          {/* ═══ Right Section: Actions ═══ */}
          <motion.div
            initial={hasAnimated ? false : { opacity: 0 }}
            animate={{ opacity: isLoading ? 0 : 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="flex items-center gap-2 md:gap-3"
          >
            {/* GitHub Link */}
            <a
              href="https://github.com/vexl-gis"
              target="_blank"
              rel="noopener noreferrer"
              className="
                hidden sm:inline-flex items-center gap-1.5
                px-3 py-1.5 rounded-full
                bg-[#12161A] hover:bg-[#171C21]
                border border-[#262C31] hover:border-[#0E93A3]/60
                text-[#EDF0F1]
                transition-all duration-300 relative z-50 group select-none
              "
              title="VEXL GitHub Organization"
            >
              <svg className="w-3.5 h-3.5 fill-current text-[#9AA5AC] group-hover:text-[#0E93A3] transition-colors flex-shrink-0" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
              </svg>
              <span className="font-mono text-[10px] font-bold tracking-wider uppercase text-[#9AA5AC] group-hover:text-[#EDF0F1] transition-colors">
                GitHub
              </span>
            </a>

            {/* CTA Button */}
            <div className="hidden sm:block">
              <Link
                to="/products/nova"
                className="
                  px-5 py-2 rounded-full 
                  bg-[#0E93A3] text-white 
                  hover:bg-[#075C68] 
                  text-xs font-semibold tracking-wide
                  transition-all duration-200 relative z-50 
                  border border-transparent 
                  shadow-[0_0_15px_rgba(14,147,163,0.3)] hover:shadow-[0_0_20px_rgba(7,92,104,0.5)] 
                  inline-flex items-center gap-1.5
                "
              >
                <span>{lang === 'ar' ? 'جرب نوفا' : 'Try Nova'}</span>
                <ArrowUpRight size={13} className="opacity-90" />
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="
              xl:hidden p-2.5 rounded-full
              bg-[#12161A] border border-[#262C31]
              text-[#EDF0F1] hover:border-[#0E93A3]/60
              transition-all duration-200
              relative z-[60]
            "
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMobileMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </motion.div>
              </AnimatePresence>
            </motion.button>
          </motion.div>

        </div>
      </motion.nav>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        navItems={navItems}
        expandedMobileMenu={expandedMobileMenu}
        setExpandedMobileMenu={setExpandedMobileMenu}
        lang={lang}
        setLang={setLang}
        theme={theme}
        toggleTheme={toggleTheme}
        isAcademy={isAcademy}
      />
    </>
  );
};

export default Navbar;

